<template>
    <div>
        <OtSplash v-if="loading" :spinner="$whitelabel.resources.spinner" />
        <div v-else>
            <CompanyPicker
                v-if="companies && username"
                :companies="companies"
                :username="username"
                :maxCompanies="maxCompanies"
                :clientName="clientName"
                @logout="logout"
                @pick="companyPicked"
            />
            <OTTFAConfirmation
                v-else-if="tfaTypes"
                :tfaTypes="tfaTypes"
                :requirePassword="false"
                :error="error"
                :submitting="!!submitting"
                @back="back"
                @logout="back"
                @submit="submitTfa"
            />
            <LoginForm
                v-else
                @submit="submit"
                @input="clearError"
                @use-sso="useSSO"
                :error="error.incorrectPassword"
                :submitting="!!submitting"
            />
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import Axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';
import type { TFAError, TFATypes } from '@openticket/vue-tfa-confirmation';
import urlJoin from 'url-join';
import LoginForm from '../components/LoginForm.vue';
import CompanyPicker from '../components/CompanyPicker.vue';
import {
    computeAutoselectDashboardQueryString,
    getAutoselectDashboardParam,
} from '../services/util/autoselectdashboard';
import { rudderAnalyticsIdentify, rudderAnalyticsTrack } from '../services/util/rudderstack';
import { getXLocation } from '../services/util/xlocation';
import OtButton from '../components/OtButton.vue';
import {
    getSignInActionOverridesQueryString,
    redirectToSSOProvider,
    resolveSignInAction,
    type SignInProvider,
} from '../services/util/signInAction';

type SessionValidationResult = { valid: false; failureType: 'redirect' | 'no-session' } | { valid: true };
const sessionValidationFailure = {
    redirect: 'redirect',
    noSession: 'no-session',
} as const;

@Component({ components: { OtButton, LoginForm, CompanyPicker } })
export default class LoginView extends Vue {

    clientConfig: {
        grant_type: string;
        client_id: string;
        state: string;
    } = {
            grant_type: 'password',
            client_id: '',
            state: '',
        };

    redirectUri: string | null = null;

    loading = true;

    submitting = 0;

    companies: [ { guid: string, name: string } ] | null = null;

    maxCompanies: number | null = null;

    clientName: string = '';

    error: TFAError = {
        incorrectPassword: null,
        incorrectTFAInput: null,
    };

    http: AxiosInstance = Axios.create({});

    password: string | null = null;

    username: string | null = null;

    tfaTypes: TFATypes | null = null;

    form: {
        email: string | null;
        password: string | null;
        tfa_type: string | null;
        tfa_input: string | null;
    } = {
            email: null,
            password: null,
            tfa_type: null,
            tfa_input: null,
        };

    async created(): Promise<void> {
        const clientId: string | null = new URLSearchParams(window.location.search).get('client_id');
        const state: string | null = new URLSearchParams(window.location.search).get('state');
        const redirectUri: string | null = new URLSearchParams(window.location.search).get('redirect_uri')
            || new URLSearchParams(window.location.search).get('redirect_url');

        if (!clientId || !state) {
            try {
                rudderAnalyticsTrack('login direct access redirect');
            } catch (e) {
                // TODO log
            }

            window.location.href = urlJoin(
                this.$whitelabel.login.fallback_app_url,
                computeAutoselectDashboardQueryString(),
                getSignInActionOverridesQueryString(),
            );

            return;
        }

        this.clientConfig.client_id = clientId;
        this.clientConfig.state = state;
        this.redirectUri = redirectUri;

        const sessionResult = await this.isSessionValid();

        if (!sessionResult.valid) {
            if (sessionResult.failureType === sessionValidationFailure.redirect) {
                // The session is not valid, but the login app should not make any assumptions.
                // This includes stopping the loading state, which should not be done.
                return;
            }

            const resolvedAction = resolveSignInAction(this.$whitelabel);
            if (resolvedAction) {
                redirectToSSOProvider(this.$whitelabel, resolvedAction);

                // There is no valid session, and the login flow should (first) be deferred to a SingleSignOn provider.
                // The loading state should not be changed.
                return;
            }

            this.loading = false;
            return;
        }

        await this.triggerIdentify();
        this.checkToken().then((isRedirect) => {
            // Wait for the navigation to complete.
            if (isRedirect) {
                return;
            }

            this.loading = false;
        }).catch(() => {
            this.loading = false;
        });
    }

    back(): void {
        this.form = {
            email: null,
            password: null,
            tfa_type: null,
            tfa_input: null,
        };

        this.tfaTypes = null;
        this.companies = null;
        this.username = null;
    }

    clearError(): void {
        this.error.incorrectPassword = null;
    }

    async submit(email: string, password: string): Promise<void> {
        if (this.form && !this.submitting) {
            this.form.email = email;
            this.form.password = password;

            rudderAnalyticsTrack('login submit');

            await this.login();
        }
    }

    async submitTfa(value: {input: string | null, password: string | null, type: string | null}): Promise<void> {
        if (this.form) {
            this.form.tfa_type = value.type;
            this.form.tfa_input = value.input;

            rudderAnalyticsTrack('login submit tfa');

            await this.login();
        }
    }

    useSSO(config: SignInProvider): void {
        redirectToSSOProvider(this.$whitelabel, config);
    }

    async logout(): Promise<void> {
        this.password = null;
        this.username = null;
        this.companies = null;
        this.form.email = null;
        this.form.password = null;

        // TODO - @openticket/lib-auth
        await this.http.get(
            urlJoin(this.$whitelabel.login.auth_api_url, 'sessions', 'logout'),
            {
                validateStatus(status) {
                    return status === 355 || (status >= 200 && status < 300);
                },
            },
        )
            .then((async (response: AxiosResponse<{}>) => {
                if (response.status === 204) {
                    return true;
                }

                if (response.status === 355) {
                    const xLocation = getXLocation(response);

                    if (window.location.href === xLocation) {
                        return true;
                    }

                    if (xLocation) {
                        // redirect to the login, even though it's an xLocation
                        await this.$router.push({
                            fullPath: xLocation,
                            query: this.$route.query,
                        });
                        return false;
                    }
                }

                return false;
            }))
            .catch((() => false));
    }

    async isSessionValid(): Promise<SessionValidationResult> {
        this.http.defaults.withCredentials = true;
        this.http.defaults.withXSRFToken = true;

        const params: { state: string; client_id: string; redirect_uri?: string } = {
            client_id: this.clientConfig.client_id,
            state: this.clientConfig.state,
        };

        if (this.redirectUri) {
            params.redirect_uri = this.redirectUri;
        }

        // TODO - @openticket/lib-auth
        // checking if we have a valid session
        return this.http.get(
            urlJoin(this.$whitelabel.login.auth_api_url, 'tokens', 'valid'),
            {
                params,
                validateStatus(status) {
                    return status === 355 || (status >= 200 && status < 300);
                },
            },
        )
            .then(((response: AxiosResponse<{}>): SessionValidationResult => {
                if (response.status === 204) {
                    return { valid: true };
                }

                if (response.status === 355) {
                    const xLocation = getXLocation(response);

                    if (window.location.href === xLocation) {
                        return { valid: true };
                    }

                    if (xLocation) {
                        window.location.href = xLocation;
                        return {
                            valid: false,
                            failureType: sessionValidationFailure.redirect,
                        };
                    }
                }

                return {
                    valid: false,
                    failureType: sessionValidationFailure.noSession,
                };
            }))
            .catch((): SessionValidationResult => ({
                valid: false,
                failureType: sessionValidationFailure.noSession,
            }));
    }

    async login(): Promise<void> {
        if (this.submitting) {
            return;
        }

        this.submitting++;
        this.http = Axios.create();
        this.http.defaults.withCredentials = true;
        this.http.defaults.withXSRFToken = true;

        interface errorAnswers {error_description: {email: [string], tfa_input: [string], tfa_type: [string]}}

        this.error = { incorrectPassword: null, incorrectTFAInput: null };

        // TODO - @openticket/lib-auth
        const successful = await this.http.post(
            urlJoin(this.$whitelabel.login.auth_api_url, 'sessions', 'login'),
            { ...this.clientConfig, ...this.form },
        )
            .then(() => true)
            .catch((error: AxiosError< errorAnswers | { error_description: string }>) => {
                console.error('login failed:', { error });
                if (error && error.response && typeof error.response.data.error_description === 'string') {
                    rudderAnalyticsTrack('login submit failed', { cause: 'server error', error: error.response.data.error_description });
                    this.error = {
                        incorrectPassword: error.response.data.error_description,
                        incorrectTFAInput: error.response.data.error_description,
                    };
                } else if (error && error.response && (
                    error.response.status === 406 || error.response.status === 422 || error.response.status === 429
                )) {
                    const { error_description } = error.response.data as errorAnswers;

                    if (error_description.email && error_description.email.length > 0) {
                        rudderAnalyticsTrack('login submit failed', { cause: 'incorrect credentials' });
                        this.error = { incorrectPassword: error_description.email[0], incorrectTFAInput: null };
                    } else if (error_description.tfa_type && error_description.tfa_type.length > 0) {
                        this.error = { incorrectPassword: null, incorrectTFAInput: null };

                        for (const e of error_description.tfa_type) {
                            let split = e.split(':');

                            if (split[0] === 'validation.in') {
                                split = split[1].split(',');

                                this.tfaTypes = {};
                                for (const tfa_method of split) {
                                    this.tfaTypes[tfa_method] = {
                                        name: tfa_method, settings: 1,
                                    };
                                }

                                rudderAnalyticsTrack('login 2fa required');
                            }
                        }
                    } else if (error_description.tfa_input && error_description.tfa_input.length > 0) {
                        this.error = { incorrectPassword: null, incorrectTFAInput: error_description.tfa_input[0] };
                    }
                }
                return false;
            });

        if (!successful) {
            this.submitting--;
            return;
        }
        const { valid } = await this.isSessionValid();

        if (!valid) {
            this.submitting--;
            return;
        }

        await this.triggerIdentify();
        this.checkToken().then((isRedirect) => {
            // Wait for the navigation to complete.
            if (isRedirect) {
                return;
            }

            this.submitting--;
        }).catch(() => {
            this.submitting--;
        });
    }

    async checkToken(): Promise<boolean> {
        const params: {
            client_id: string;
            response_type: string;
            redirect_uri?: string;
            state?: string;
            autoselect_dashboard?: string;
        } = {
            client_id: this.clientConfig.client_id,
            response_type: 'code',
        };

        if (this.redirectUri) {
            params.redirect_uri = this.redirectUri;
        }

        if (this.clientConfig.state) {
            params.state = this.clientConfig.state;
        }

        if (new URLSearchParams(window.location.search).has('autoselect_dashboard')) {
            params.autoselect_dashboard = getAutoselectDashboardParam();
        }

        return this.http.get(
            urlJoin(this.$whitelabel.login.auth_api_url, 'tokens', 'check'),
            {
                params,
                validateStatus(status) {
                    return status === 355 || (status >= 200 && status < 300);
                },
            },
        )
            .then(((response: AxiosResponse<{
                companies: [{ guid: string, name: string }],
                max_company_count: number, user: { name: string }, client_name: string
            }>) => {
                if (response.status === 200) {
                    // Necessary to select companies
                    this.companies = response.data.companies;
                    this.username = response.data.user.name;
                    this.maxCompanies = response.data.max_company_count;
                    this.clientName = response.data.client_name;
                }

                if (response.status === 355) {
                    // Redirect to location
                    const xLocation = getXLocation(response);

                    if (window.location.href !== xLocation && xLocation) {
                        window.location.href = xLocation;
                        return true;
                    }
                }

                return false;
            }))
            .catch((error: AxiosError<{ error_description: string }>) => {
                console.error('authorize failed:', { error });
                if (error && error.response && error.response.status === 401) {
                    this.error = {
                        incorrectPassword: error.response.data.error_description,
                        incorrectTFAInput: error.response.data.error_description,
                    };
                } else {
                    this.error = {
                        incorrectPassword: this.$t('login.login.generic_error') as string,
                        incorrectTFAInput: this.$t('login.login.generic_error') as string,
                    };
                }
                throw error;
            });
    }

    async companyPicked(companyIds: string[]): Promise<void> {
        const urlParts: string[] = [
            'tokens',
            'authorize',
            `?client_id=${this.clientConfig.client_id}`,
            '&response_type=code',
        ];

        if (this.redirectUri) {
            urlParts.push(`&redirect_uri=${this.redirectUri}`);
        }

        if (this.clientConfig.state) {
            urlParts.push(`&state=${this.clientConfig.state}`);
        }

        // TODO - @openticket/lib-auth
        await this.http.post(
            urlJoin(this.$whitelabel.login.auth_api_url, ...urlParts),
            {
                company_ids: companyIds,
            },
            {
                validateStatus(status) {
                    return status === 355 || (status >= 200 && status < 300);
                },
            },
        )
            .then(((response: AxiosResponse<{
                companies: [{ guid: string, name: string }],
                max_company_count: number, user: { name: string }
            }>) => {
                if (response.status === 355) {
                // Redirect to location
                    const xLocation = getXLocation(response);

                    if (window.location.href !== xLocation && xLocation) {
                        window.location.href = xLocation;
                    }
                }
            }))
            .catch((e: unknown) => {
                console.error('company picked login error:', { e }); // TODO
                this.$notifications.warning(this.$t('auth.error.unexpected'));
            });
    }

    async triggerIdentify(): Promise<void> {
        try {
            await this.http.get(
                urlJoin(urlJoin(this.$whitelabel.login.auth_api_url, 'sessions', 'me')),
                {
                    timeout: 1500, // reasonable timeout
                },
            ).then((sessionResponse: AxiosResponse<{ guid: string; }>) => {
                // Fetched info, identify / track
                if (sessionResponse.data && sessionResponse.data.guid) {
                    rudderAnalyticsIdentify(sessionResponse.data.guid, sessionResponse.data);
                    rudderAnalyticsTrack('login auth success');
                }
            });
        } catch (e) {
            console.error('identify failed:', { e });
        }
    }

}
</script>
